import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { createAppColumnHelper, useClientTable, type AppColumnDef } from "../../../components/table/tableFeatures";
import { RT8Pagination } from "../../../components/table/Pagination";
import { css } from "../../../../styled-system/css";
import { Table } from "../../../components/table/Table";
import { useUserOrgPermissions } from "../../admin/api/useUserOrgPermissions";
import Loading from "../../../components/core/blocks/Loading";
import { type UserOrgRoleMembership } from "../../../zodios/apiDefinitions/api/userManagement";
import { selectionStateToSelectedIds } from "../../../components/table/utils";
import { subject } from "@casl/ability";
import { useAbility, useSimpleCan } from "../../../config/casl/casl";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import { useUserRoleOptions } from "../api/useUserRoleOptions";
import { Select } from "../../../components/fields/base/Select";
import { UserRolesTooltip, getRoleLabel, type RoleName } from "./OrgUsersTable";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Switch } from "../../../components/fields/base/Switch";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import { OrgCell } from "../../organization/OrgCell";
import { FaCheck } from "react-icons/fa6";
import { Button } from "../../../components/core/basic/Button";
import { Modal } from "../../../components/core/basic/Modal";
import OrgSelectionTable from "../../../components/custom/staff/OrgSelectionTable";
import { type OrgRoleSelection } from "../types";
import { DEFAULT_ROLE_NAME, ROLES } from "../constants";
import { type Organization } from "../../../zodios/common/orgSchema";
import { useManagedUserOrgRoles } from "../api/useManagedUserOrgRoles";
import { useBulkMutateUserRolesMutation } from "../api/useBulkMutateUserRolesMutation";
import AgencyFilter from "./AgencyFilter";
import { useAppSelector } from "../../../redux/hooks";

const columnHelper = createAppColumnHelper<UserOrgRoleMembership>();
const emptyData: UserOrgRoleMembership[] = [];

type RoleCellProps = {
  userId?: string;
  row: { original: UserOrgRoleMembership };
  roleOptions: { label: string; value: string }[];
};

const RoleCell = ({ row, roleOptions, userId }: RoleCellProps) => {
  const [role, setRole] = useState(row.original.role_name);

  const ability = useAbility();
  const bulkMutateUserRolesMutation = useBulkMutateUserRolesMutation();

  return (
    <Flex className={css({ gap: "medium", alignItems: "center", paddingY: "xsmall", paddingX: "medium" })}>
      <Select
        onSelectionChange={(value) => {
          if (value) {
            setRole(String(value));
          }
        }}
        selectedKey={role}
        items={roleOptions}
        css={{ width: "150px" }}
        isDisabled={!ability.can("update", subject("User", { org_id: row.original.org.id }))}
      />
      <UserRolesTooltip />
      <Button
        onPress={() => {
          if (!role || !userId) {
            return;
          }

          bulkMutateUserRolesMutation.mutate({
            updates: [
              {
                membership_id: row.original.id,
                role_name: role,
              },
            ],
            creates: [],
            upserts: [],
            deletes: [],
          });
        }}
        size={"small"}
        isDisabled={role === row.original.role_name}
      >
        <FaCheck />
      </Button>
    </Flex>
  );
};

const UserPermissionsSection = ({ userId, persistKey }: { userId: string; persistKey: string }) => {
  const { t } = useTranslation(["user_management", "common"]);
  const user = useAppSelector((state) => state.user);

  const defaultAgencyId = user.account_manager.agency_id ?? "none";
  const defaultShowAgencyFilter = user.is_staff && defaultAgencyId !== "none";

  const [selectedRole, setSelectedRole] = useState<null | string>(null);
  const [showAddPermissionsModal, setShowAddPermissionsModal] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [orgRoles, setOrgRoles] = useState<OrgRoleSelection>({});
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | "none">(defaultAgencyId);
  const [showAgencyFilter, setShowAgencyFilter] = useState(defaultShowAgencyFilter);
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false);

  const userCanUpdateUsers = useSimpleCan("update", "User");
  const enableBulkPermissionsManagement = userCanUpdateUsers;

  const userPermissionsQuery = useUserOrgPermissions(parseInt(userId), { enabled: !!userId });
  const userDetails = userPermissionsQuery.data?.user_profile;

  const ability = useAbility();

  const userRoleOptionsQuery = useUserRoleOptions();

  const bulkMutateUserRolesMutation = useBulkMutateUserRolesMutation();

  const userOrgRolesQuery = useManagedUserOrgRoles(
    selectedAgencyId !== "none" && showAgencyFilter ?
      {
        agencyId: selectedAgencyId,
      }
    : {},
    {},
  );
  const memberships = userPermissionsQuery.data?.memberships;
  const hasHiddenOrgMemberships = memberships?.some((membership) => !membership.org.is_visible) ?? false;

  const visibleMemberships = useMemo(() => {
    if (!memberships) {
      return emptyData;
    }

    return showHiddenOrgs ? memberships : memberships.filter((membership) => membership.org.is_visible);
  }, [memberships, showHiddenOrgs]);

  const availableUserOrgs = useMemo(() => {
    if (!userOrgRolesQuery.data) {
      return [];
    }

    const userOrgIdsWithAssignedPermissions =
      userPermissionsQuery.data?.memberships.map((membership) => membership.org.id) ?? [];

    return userOrgRolesQuery.data.orgs.filter((org) => !userOrgIdsWithAssignedPermissions.includes(org.id));
  }, [userOrgRolesQuery.data, userPermissionsQuery.data]);

  const roleOptions = useMemo(() => {
    if (!userRoleOptionsQuery.data) {
      return [];
    }
    return userRoleOptionsQuery.data.roles
      .map((role) => ({
        label: getRoleLabel(t, role.role_name as RoleName),
        value: role.role_name,
      }))
      .filter((role) => Boolean(role.label));
  }, [userRoleOptionsQuery.data, t]);

  const localizedRoles = useMemo(
    () =>
      ROLES.map((r) => ({
        label: getRoleLabel(t, r.value as RoleName),
        value: r.value,
      })),
    [t],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("org.name", {
        header: t(($) => $.user_management["Organization"]),
        enableHiding: false,
        enableSorting: true,
        cell: (props) => {
          return <OrgCell orgData={props.row.original.org} changeOrgOnClick={false} />;
        },
        size: 350,
      }),
      columnHelper.accessor("role_name", {
        header: t(($) => $.user_management["Role"]),
        enableHiding: false,
        enableSorting: true,
        size: 300,
        minSize: 300,
        maxSize: 300,
        cell: (props) => {
          return (
            <RoleCell
              row={props.row}
              roleOptions={roleOptions}
              userId={userId}
              key={`${props.row.original.id}-${props.row.original.role_name}`}
            />
          );
        },
      }),
      columnHelper.accessor("org.id", {
        header: t(($) => $.user_management["Organization ID"]),
        enableHiding: false,
        enableSorting: true,
        cell: ({ getValue }) => {
          return getValue();
        },
      }),
    ];
  }, [roleOptions, userId, t]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
      sorting: [{ id: "org.id", desc: true }],
    },
  });

  const table = useClientTable({
    data: visibleMemberships,
    columns,
    getRowId: (row) => row.id.toString(),
    enableRowSelection: (row) => {
      return ability.can("update", subject("User", { org_id: row.original.org.id }));
    },
    enableGlobalFilter: true,
    initialState,
    state,
    ...setters,
  });

  const handleOrgChange = (ids: number[]) => {
    setSelectedOrgIds(ids);

    setOrgRoles((prev) => {
      const updatedOrgRoles: Record<number, string> = Object.fromEntries(
        ids.map((id) => [id, prev[id] ?? DEFAULT_ROLE_NAME]),
      );

      return updatedOrgRoles;
    });
  };

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const handleShowHiddenOrgsChange = (isSelected: boolean) => {
    setShowHiddenOrgs(isSelected);

    if (!isSelected) {
      const visibleMembershipIds = new Set(
        memberships?.filter((membership) => membership.org.is_visible).map((membership) => membership.id.toString()),
      );

      table.setRowSelection((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([membershipId]) => visibleMembershipIds.has(membershipId))),
      );
    }
  };

  const reset = () => {
    setShowAddPermissionsModal(false);
    setSelectedOrgIds([]);
    setOrgRoles({});
    table.resetRowSelection();
  };

  const RoleColumn = useMemo<AppColumnDef<Organization>>(
    () => ({
      id: "role",
      header: t(($) => $.user_management["Role"]),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) => {
        const orgId = row.original.id;

        if (!selectedOrgIds.includes(orgId)) {
          return null;
        }

        return (
          <Select
            items={localizedRoles}
            selectedKey={orgRoles[orgId]}
            onSelectionChange={(value) => {
              if (!value) return;

              setOrgRoles((prev) => ({
                ...prev,
                [orgId]: String(value),
              }));
            }}
            css={{ width: "150px", paddingX: "large" }}
          />
        );
      },
    }),
    [orgRoles, selectedOrgIds, localizedRoles, t],
  );

  if (!userCanUpdateUsers) {
    return <UnauthorizedWarning />;
  }

  return (
    <>
      {showAddPermissionsModal && (
        <Modal
          isOpen={true}
          title={t(($) => $.user_management["Add New User Permissions"])}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              reset();
            }
          }}
        >
          <VStack className={css({ width: "60vw", padding: "xlarge" })}>
            {user.is_staff && (
              <AgencyFilter
                show={showAgencyFilter}
                value={selectedAgencyId}
                onToggle={() => {
                  setSelectedOrgIds([]);
                  setOrgRoles({});
                  setSelectedAgencyId("none");
                  setShowAgencyFilter((prev) => !prev);
                }}
                onChange={(agencyId) => {
                  setSelectedOrgIds([]);
                  setOrgRoles({});
                  setSelectedAgencyId(agencyId);
                }}
              />
            )}
            {availableUserOrgs.length === 0 && !userOrgRolesQuery.isLoading ?
              <>
                <p className={css({ maxWidth: "75%", paddingY: "large", textAlign: "center" })}>
                  {t(
                    ($) =>
                      $.user_management[
                        "No available organizations to add permissions for the selected user. This usually means the user has permissions for all organizations in the account manager or agency."
                      ],
                  )}
                </p>
                <Flex gap={"xlarge"} alignItems={"center"}>
                  <Button onPress={() => setShowAddPermissionsModal(false)}>
                    {t(($) => $.common.actions.Continue)}
                  </Button>
                </Flex>
              </>
            : <div className={css({ width: "800px", maxHeight: "60vh", paddingRight: "large", overflowY: "auto" })}>
                <OrgSelectionTable
                  onChange={handleOrgChange}
                  selectedOrgIds={selectedOrgIds}
                  displaySelectOrgFilters={false}
                  additionalColumns={[RoleColumn]}
                  orgs={availableUserOrgs}
                  isLoading={userOrgRolesQuery.isLoading || userPermissionsQuery.isLoading}
                  enableHiddenOrgsFilter
                />
                {selectedOrgIds.length ?
                  <p className={css({ maxWidth: "75%", marginX: "auto", paddingTop: "large", textAlign: "center" })}>
                    {t(
                      ($) =>
                        $.user_management[
                          "Are you sure you want to assign the selected roles to {firstName} {lastName} ({email}) for the selected organizations?"
                        ],
                      {
                        firstName: userDetails?.first_name ?? "",
                        lastName: userDetails?.last_name ?? "",
                        email: userDetails?.email ?? "",
                        count: selectedOrgIds.length,
                      },
                    )}
                  </p>
                : null}
                <Flex gap={"xlarge"} justifyContent={"center"} alignItems={"center"} paddingTop={"xlarge"}>
                  <Button onPress={() => setShowAddPermissionsModal(false)} variant={"secondary"}>
                    {t(($) => $.common.actions.Cancel)}
                  </Button>
                  <Button
                    onPress={() => {
                      if (userId) {
                        const body = {
                          upserts: Object.entries(orgRoles).map(([orgId, roleName]) => ({
                            user_id: Number(userId),
                            org_id: Number(orgId),
                            role_name: roleName,
                          })),
                          creates: [],
                          updates: [],
                          deletes: [],
                        };

                        bulkMutateUserRolesMutation.mutate(body, { onSuccess: () => reset() });
                      }
                    }}
                    isDisabled={selectedOrgIds.length === 0}
                  >
                    {t(($) => $.common.actions.Save)}
                  </Button>
                </Flex>
              </div>
            }
          </VStack>
        </Modal>
      )}
      {userPermissionsQuery.isLoading ?
        <Loading fullPage={false} height={"300px"} />
      : <>
          <Flex gap={"large"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: { base: "0 1 150px", md: "0 1 220px" } }}
              value={state.globalFilter ?? ""}
              onChange={(value) => {
                setters.onGlobalFilterChange(value);
              }}
            />
            <Flex gap={"large"} justifyContent={"flex-end"} alignItems={"center"}>
              {enableBulkPermissionsManagement && (
                <Button
                  onPress={() => {
                    setShowAddPermissionsModal(true);
                  }}
                >
                  <span className={css({ fontSize: "small" })}>{t(($) => $.user_management["Add Roles"])}</span>
                </Button>
              )}
              <BulkActionButton
                selected={selected}
                options={[
                  {
                    label: t(($) => $.user_management["Update User Roles"]),
                    action: () => {
                      if (selectedRole && userId) {
                        bulkMutateUserRolesMutation.mutate({
                          updates: selected.map((membershipId) => ({
                            membership_id: Number(membershipId),
                            role_name: selectedRole,
                          })),
                          creates: [],
                          upserts: [],
                          deletes: [],
                        });
                      }
                    },
                    actionFormFields: (
                      <Select
                        label={t(($) => $.user_management["New Role"])}
                        items={roleOptions}
                        selectedKey={selectedRole}
                        onSelectionChange={(v) => {
                          if (v) {
                            setSelectedRole(String(v));
                          }
                        }}
                      />
                    ),
                    confirmationMessage: (
                      <p>
                        {t(
                          ($) =>
                            $.user_management[
                              "Are you sure you want to update this user's role in the selected organizations?"
                            ],
                          { count: selected.length },
                        )}
                      </p>
                    ),
                    disableConfirmButton: !selectedRole,
                  },
                  {
                    label: t(($) => $.user_management["Delete User Roles"]),
                    action: () => {
                      if (userId) {
                        bulkMutateUserRolesMutation.mutate({
                          deletes: selected.map((membershipId) => ({
                            membership_id: Number(membershipId),
                          })),
                          creates: [],
                          upserts: [],
                          updates: [],
                        });
                      }
                    },
                    confirmationMessage: (
                      <p>
                        {t(
                          ($) =>
                            $.user_management[
                              "Are you sure you want to delete this user's role in the selected organizations?"
                            ],
                          { count: selected.length },
                        )}
                      </p>
                    ),
                  },
                ]}
              />
              <RT8Pagination table={table} canSetPageSize={true} />
            </Flex>
          </Flex>
          {hasHiddenOrgMemberships && (
            <Flex width={"100%"} paddingTop={"small"} paddingBottom={"xsmall"}>
              <Switch
                isSelected={showHiddenOrgs}
                onChange={handleShowHiddenOrgsChange}
                className={css({ whiteSpace: "nowrap" })}
              >
                {t(($) => $.user_management["Show Hidden Orgs"])}
              </Switch>
            </Flex>
          )}
          <Table table={table} />
        </>
      }
    </>
  );
};

export default UserPermissionsSection;
